import { ref, computed } from '#imports';
import type { ItemList, SelectModel } from '@/shared/types';
import type { SelectProps as Props } from '@/shared/interfaces';

export default (props: Props<SelectModel>, emit: any) => {
	const field = ref<HTMLInputElement | null>(null);
	const isOpen = ref(false);
	const isInput = ref(false);

	const computedClass = computed(() => ({
		'field--open': isOpen.value,
		'field--edit': isInput.value,
		'field--error': props.isError,
		'field--required': props.required,
		'field--disabled': props.disabled,
		[`field--${props.color}`]: props.color,
	}));

	const computedValue = computed({
		get() {
			const modelType = typeof props.modelValue;

			if (['number', 'string'].includes(modelType)) {
				return props.options.find((el: ItemList) => el.id === props.modelValue)?.value;
			}

			return props.modelValue;
		},
		set(val: SelectModel) {
			emit('update:modelValue', val);
		},
	});

	const computedTitle = computed(() => {
		return typeof computedValue.value === 'string' ? String(computedValue.value) : '';
	});

	const computedEdditable = computed(() => {
		return !isInput.value && props.isEdditable;
	});

	const computedInput = computed(() => {
		return isInput.value && props.isEdditable;
	});

	const computedListeners = computed(() => {
		const listeners: Record<string, unknown> = {};

		if (isInput.value) {
			listeners.input = handleInput;
			listeners.keydown = handleClose;
		} else {
			listeners.click = handleToggle;
		}

		return listeners;
	});

	const optionClass = (option: ItemList) => ({
		'field__select-item--active': option.id === props.modelValue,
		'field__select-item--disabled': option?.disabled,
	});

	const handleInput = (e: Event) => {
		const el = e.target as HTMLInputElement;
		computedValue.value = String(el.value);
	};

	const handleClose = () => {
		isOpen.value = false;
	};

	const handleToggle = () => {
		isOpen.value = !isOpen.value;
	};

	const handleOption = (option: ItemList): void => {
		if (option?.disabled) return;

		computedValue.value = option.id;
		isInput.value = false;
		handleClose();
	};

	const handleInputMode = () => {
		computedValue.value = '';
		isInput.value = true;
		handleClose();

		setTimeout(() => {
			field.value?.focus();
		}, 0);
	};

	return {
		field,
		isOpen,
		isInput,
		computedClass,
		computedValue,
		computedTitle,
		computedEdditable,
		computedInput,
		computedListeners,
		optionClass,
		handleClose,
		handleToggle,
		handleOption,
		handleInputMode,
	};
};
